import { useRef } from 'react';
import { useDrag } from 'react-dnd';
import { clsx } from 'clsx';
import { LinkIcon, PlayIcon } from '@heroicons/react/24/outline';
import type { App, Position } from '../../types';

interface DraggableAppNodeProps {
  app: App;
  position: Position;
  isConnecting: boolean;
  isConnectionStart: boolean;
  onMove: (position: Position) => void;
  onStartConnection: () => void;
  onEndConnection: () => void;
  readonly?: boolean;
}

export function DraggableAppNode({
  app,
  position,
  isConnecting,
  isConnectionStart,
  onMove,
  onStartConnection,
  onEndConnection,
  readonly = false
}: DraggableAppNodeProps) {
  const nodeRef = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag] = useDrag({
    type: 'canvas-app',
    item: { app, initialPosition: position },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (item, monitor) => {
      if (!monitor.didDrop() && nodeRef.current) {
        const delta = monitor.getDifferenceFromInitialOffset();
        if (delta) {
          const newPosition = {
            x: position.x + delta.x,
            y: position.y + delta.y
          };
          onMove(newPosition);
        }
      }
    },
    canDrag: !readonly,
  });

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (isConnecting) {
      onEndConnection();
    }
  };

  const handleConnectionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!readonly) {
      onStartConnection();
    }
  };

  return (
    <div
      ref={(node) => {
        nodeRef.current = node;
        drag(node);
      }}
      className={clsx(
        'absolute select-none transition-all duration-200',
        {
          'opacity-50': isDragging,
          'z-50': isConnectionStart,
          'z-40': isConnecting && !isConnectionStart,
          'z-30': !isConnecting,
        }
      )}
      style={{
        left: position.x,
        top: position.y,
        transform: 'translate(-50%, -50%)',
      }}
      onClick={handleClick}
    >
      <div
        className={clsx(
          'bg-white dark:bg-gray-800 rounded-xl shadow-lg border-2 p-4 min-w-[200px] transition-all duration-200',
          {
            'border-blue-500 shadow-blue-500/25': isConnectionStart,
            'border-green-300 shadow-green-500/25 cursor-pointer': isConnecting && !isConnectionStart,
            'border-gray-200 dark:border-gray-700': !isConnecting,
            'hover:shadow-xl hover:border-blue-300': !readonly && !isConnecting,
            'cursor-move': !readonly && !isConnecting,
            'cursor-crosshair': isConnecting,
          }
        )}
      >
        {/* App Header */}
        <div className="flex items-center space-x-3 mb-3">
          <img
            src={app.icon_url}
            alt={app.name}
            className="h-8 w-8 rounded-lg"
          />
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-gray-900 dark:text-white truncate">
              {app.name}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {app.category}
            </p>
          </div>
        </div>

        {/* App Description */}
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 line-clamp-2">
          {app.description}
        </p>

        {/* Connection Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {/* Trigger Indicator */}
            <div className="flex items-center space-x-1 text-xs text-blue-600 dark:text-blue-400">
              <PlayIcon className="h-3 w-3" />
              <span>Trigger</span>
            </div>
          </div>

          {/* Connection Button */}
          {!readonly && (
            <button
              onClick={handleConnectionClick}
              className={clsx(
                'p-2 rounded-lg transition-colors duration-200',
                {
                  'bg-blue-500 text-white shadow-lg': isConnectionStart,
                  'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600': !isConnectionStart,
                }
              )}
              title={isConnectionStart ? 'Click another app to connect' : 'Start connection'}
            >
              <LinkIcon className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Connection Points */}
        <div className="absolute -inset-2 pointer-events-none">
          {/* Top */}
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-blue-500 rounded-full opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
          {/* Right */}
          <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-blue-500 rounded-full opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
          {/* Bottom */}
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 w-3 h-3 bg-blue-500 rounded-full opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
          {/* Left */}
          <div className="absolute left-0 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-blue-500 rounded-full opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
        </div>
      </div>
    </div>
  );
}